import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PublicSiteHeader } from "@/components/layout/public-site-header";
import { LandingSignupProvider } from "@/pages/landing-signup-context";
import { LandingSections } from "@/pages/landing-sections";
import { customInstance } from "@/lib/axios";
import { toast } from "sonner";

/** Format up to 10 digits as xxx-xxx-xxxx. */
function formatPhoneDisplay(digits: string): string {
  const d = digits.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Normalize phone input: digits only, drop leading 1, max 10. */
function normalizePhoneInput(raw: string): string {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.length > 0 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.slice(0, 10);
}

function useDealerCount() {
  return useMemo(() => {
    const startCount = 450;
    const startDate = new Date("2026-07-17T00:00:00");
    const now = new Date();
    const monthsElapsed =
      (now.getFullYear() - startDate.getFullYear()) * 12 +
      (now.getMonth() - startDate.getMonth()) -
      (now.getDate() < startDate.getDate() ? 1 : 0);
    return (startCount + Math.max(0, monthsElapsed) * 25).toLocaleString();
  }, []);
}

const SCROLL_SPY_SECTIONS = ["home", "platform", "how-it-works", "benefits", "industries", "faq", "contact"];

const SIGNUP_ATTRIBUTION_KEY = "dealerdog_signup_attribution";

type SignupAttribution = {
  referral_token?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
};

function readSignupAttribution(): SignupAttribution {
  try {
    const raw = sessionStorage.getItem(SIGNUP_ATTRIBUTION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as SignupAttribution;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Store ref/UTM from the current URL. Leave an existing session value when this URL has none. */
function captureSignupAttributionFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const ref = (params.get("ref") || "").trim();
  const utmSource = (params.get("utm_source") || "").trim();
  const utmMedium = (params.get("utm_medium") || "").trim();
  const utmCampaign = (params.get("utm_campaign") || "").trim();
  if (!ref && !utmSource && !utmMedium && !utmCampaign) return;
  const next: SignupAttribution = {};
  if (ref) next.referral_token = ref;
  if (utmSource) next.utm_source = utmSource;
  if (utmMedium) next.utm_medium = utmMedium;
  if (utmCampaign) next.utm_campaign = utmCampaign;
  sessionStorage.setItem(SIGNUP_ATTRIBUTION_KEY, JSON.stringify(next));
}

export function LandingPage() {
  const { user } = useAuth();
  const dealerCount = useDealerCount();
  const year = new Date().getFullYear();
  const [showSignUp, setShowSignUp] = useState(false);
  const [form, setForm] = useState({
    organization_name: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    captureSignupAttributionFromUrl();
    if (window.location.hash === "#create-account") {
      setShowSignUp(true);
    }
  }, []);

  if (user) {
    return <Navigate to="/apps" replace />;
  }

  const openSignup = () => setShowSignUp(true);

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const phoneDigits = (form.phone || "").replace(/\D/g, "");
    if (!phoneDigits) {
      setErrors({ phone: "Phone number is required." });
      return;
    }
    if (phoneDigits.length !== 10) {
      setErrors({ phone: "Enter a valid 10-digit phone number." });
      return;
    }
    setIsSubmitting(true);
    try {
      const attribution = readSignupAttribution();
      await customInstance.post("/api/dealer-signup/", {
        ...form,
        phone: phoneDigits,
        ...attribution,
      });
      setSubmitted(true);
      setForm({ organization_name: "", first_name: "", last_name: "", email: "", phone: "" });
      toast.success("Thanks for signing up! We'll be in touch shortly.");
    } catch (err: unknown) {
      const data = (err as { response?: { data?: unknown } }).response?.data;
      if (data && typeof data === "object") {
        const next: Record<string, string> = {};
        for (const [k, v] of Object.entries(data)) {
          next[k] = Array.isArray(v) ? (v[0] as string) : String(v);
        }
        setErrors(next);
        toast.error("Please fix the errors and try again.");
      } else {
        toast.error("Sign-up failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LandingSignupProvider openSignup={openSignup}>
      <Helmet>
        <title>DealerDog | Dealer-First Equipment Financing</title>
        <meta
          name="description"
          content="DealerDog helps equipment dealers prequalify commercial buyers, present financing paths, and keep more deals moving."
        />
        <meta name="theme-color" content="#0f172a" />
        <meta property="og:title" content="DealerDog | Turn More Shoppers Into Funded Buyers" />
        <meta
          property="og:description"
          content="Dealer-first equipment financing built to keep sales moving."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="dd-public-page">
        <PublicSiteHeader
          variant="home"
          onCreateAccount={openSignup}
          scrollSpySectionIds={SCROLL_SPY_SECTIONS}
        />

        <main id="main-content">
          <LandingSections dealerCount={dealerCount} year={year} />
        </main>

        {showSignUp ? (
          <div
            className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50"
            onClick={() => !isSubmitting && setShowSignUp(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-900">Create an account</h2>
              <p className="mt-1 text-sm text-gray-600">
                Sign up as a dealer. We’ll assign an account manager and get you set up to submit and
                prequalify deals.
              </p>
              {submitted ? (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                  <p className="font-medium">Thanks for signing up!</p>
                  <p className="mt-1 text-sm">
                    We’ll be in touch shortly to complete your setup and assign your account manager.
                  </p>
                  <Button
                    className="mt-4"
                    variant="outline"
                    onClick={() => {
                      setSubmitted(false);
                      setShowSignUp(false);
                    }}
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSignUpSubmit} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="org_name">Dealer / organization name</Label>
                    <Input
                      id="org_name"
                      value={form.organization_name}
                      onChange={(e) => setForm((f) => ({ ...f, organization_name: e.target.value }))}
                      placeholder="Acme Equipment Co."
                      className="mt-1"
                    />
                    {errors.organization_name ? (
                      <p className="text-red-500 text-sm mt-1">{errors.organization_name}</p>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="first_name">First name</Label>
                      <Input
                        id="first_name"
                        value={form.first_name}
                        onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                        placeholder="Jane"
                        className="mt-1"
                      />
                      {errors.first_name ? (
                        <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
                      ) : null}
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last name</Label>
                      <Input
                        id="last_name"
                        value={form.last_name}
                        onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                        placeholder="Doe"
                        className="mt-1"
                      />
                      {errors.last_name ? (
                        <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="jane@acme.com"
                      className="mt-1"
                    />
                    {errors.email ? <p className="text-red-500 text-sm mt-1">{errors.email}</p> : null}
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={(e) => {
                        const digits = normalizePhoneInput(e.target.value);
                        setForm((f) => ({ ...f, phone: formatPhoneDisplay(digits) }));
                      }}
                      placeholder="555-555-5555"
                      maxLength={12}
                      className="mt-1"
                      required
                    />
                    {errors.phone ? <p className="text-red-500 text-sm mt-1">{errors.phone}</p> : null}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button type="submit" disabled={isSubmitting} className="flex-1">
                      {isSubmitting ? "Submitting…" : "Sign up"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowSignUp(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 pt-1">
                    By signing up, you acknowledge our{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-gray-700"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </LandingSignupProvider>
  );
}
